if (typeof customElements.get('newsletter-popup') === 'undefined') {
  const NEWSLETTER_POPUP_FREQUENCY = {
    day: 86400000,
    week: 604800000,
    month: 2592000000,
  };

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-popup-modal]');
      this.teaser = this.querySelector('[data-popup-teaser]');
      this.form = document.getElementById(this.dataset.formId);
      this.contestContactForm = document.getElementById(this.dataset.contestContactId);
      this.contactKey = `contest-only-popup-${this.dataset.sectionId}`;
      this.storageKey = `newsletter-popup-${this.dataset.sectionId}${this.dataset.campaignId ? `-${this.dataset.campaignId}` : ""}`;
      this.submittedKey = `${this.storageKey}-submitted`;

      this.onKeydown = (event) => {
        if (event.key === 'Escape') {
          this.close();
          return;
        }
        if (event.key === 'Tab' && this.isOpen) {
          const focusable = Array.from(this.modal.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')).filter((el) => el.getClientRects().length);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && (document.activeElement === first || !this.modal.contains(document.activeElement))) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && (document.activeElement === last || !this.modal.contains(document.activeElement))) {
            event.preventDefault();
            first?.focus();
          }
        }
      };

      this.querySelectorAll('[data-popup-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });
      this.querySelectorAll('[data-popup-open]').forEach((el) => {
        el.addEventListener('click', () => this.open());
      });
      this.querySelector('[data-popup-teaser-close]')?.addEventListener('click', () => {
        this.saveState({ teaserClosed: true });
        this.toggleTeaser(false);
      });
      this.form?.addEventListener('submit', () => {
        try { sessionStorage.setItem(this.submittedKey, '1'); } catch (e) {}
      });

      this.querySelectorAll('[data-contest-mode]').forEach((button) => {
        button.addEventListener('click', () => this.setContestMode(button.dataset.contestMode === 'only', true));
      });
      this.contestContactForm?.addEventListener('submit', () => {
        try { sessionStorage.setItem(this.contactKey, '1'); } catch (e) {}
      });
      let contactSubmitted = false;
      try { contactSubmitted = sessionStorage.getItem(this.contactKey) === '1'; sessionStorage.removeItem(this.contactKey); } catch (e) {}
      if (contactSubmitted && (this.querySelector('[data-contest-contact-success]') || this.querySelector('[data-contest-contact-error]'))) {
        this.setContestMode(true, false);
        this.open();
        return;
      }

      if (this.hasAttribute('data-design-mode')) {
        this.bindEditorEvents();
        return;
      }

      let submitted = false;
      try { submitted = sessionStorage.getItem(this.submittedKey) === '1'; sessionStorage.removeItem(this.submittedKey); } catch (e) {}

      // Other customer forms on the page (e.g. footer newsletter) share the posted state
      if (this.querySelector('[data-popup-success]')) {
        if (submitted) {
          this.saveState({ subscribed: true });
          this.open();
        }
        return;
      }

      const error = this.querySelector('[data-popup-error]');
      if (error && submitted) {
        this.open();
        return;
      }
      error?.remove();

      if (this.state.subscribed) {
        return;
      }

      if (this.isDue()) {
        this.preloadImage();
        this.timer = setTimeout(() => this.open(), parseInt(this.dataset.delay, 10) * 1000);
      } else {
        this.toggleTeaser(true);
      }
    }

    setContestMode(only, moveFocus) {
      const marketing = this.querySelector('[data-contest-marketing]');
      const contact = this.querySelector('[data-contest-only]');
      const toggle = this.querySelector('[data-contest-mode="only"]');
      if (!marketing || !contact) return;
      marketing.hidden = only;
      contact.hidden = !only;
      const benefit = this.querySelector('[data-contest-benefit]');
      if (benefit) benefit.hidden = only;
      if (toggle) { toggle.hidden = only; toggle.setAttribute('aria-expanded', String(only)); }
      if (moveFocus) (only ? contact.querySelector('input[type="email"]') : this.modal)?.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      if (this.isOpen) {
        document.body.classList.remove('modal-opened');
      }
    }

    get state() {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey)) || {};
      } catch (e) {
        return {};
      }
    }

    saveState(data) {
      if (this.hasAttribute('data-design-mode')) {
        return;
      }
      try { localStorage.setItem(this.storageKey, JSON.stringify({ ...this.state, ...data })); } catch (e) {}
    }

    isDue() {
      const { closedAt } = this.state;
      if (!closedAt) {
        return true;
      }
      const interval = NEWSLETTER_POPUP_FREQUENCY[this.dataset.frequency];
      return interval ? Date.now() - closedAt > interval : false;
    }

    preloadImage() {
      const image = this.modal.querySelector('img[loading="lazy"]');
      if (image) {
        image.loading = 'eager';
      }
    }

    open() {
      if (this.isOpen) {
        return;
      }
      clearTimeout(this.timer);
      this.isOpen = true;
      this.lastFocused = document.activeElement;
      this.preloadImage();
      this.toggleTeaser(false);

      this.modal.hidden = false;
      document.body.classList.add('modal-opened');
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => {
        this.modal.classList.add('is-active');
        (this.modal.querySelector('.newsletter-popup__input:not([disabled])') || this.modal).focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) {
        return;
      }
      this.isOpen = false;

      this.modal.classList.remove('is-active');
      document.body.classList.remove('modal-opened');
      document.removeEventListener('keydown', this.onKeydown);
      setTimeout(() => {
        this.modal.hidden = true;
      }, 200);
      this.lastFocused?.focus?.({ preventScroll: true });

      this.saveState({ closedAt: Date.now() });
      this.toggleTeaser(true);
    }

    toggleTeaser(show) {
      if (!this.teaser) {
        return;
      }
      const state = this.state;
      this.teaser.hidden = !show || state.subscribed || state.teaserClosed;
    }

    bindEditorEvents() {
      this.onSectionSelect = (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) {
          window.newsletterPopupEditorOpen = true;
          this.open();
        }
      };
      this.onSectionDeselect = (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) {
          window.newsletterPopupEditorOpen = false;
          this.close();
        }
      };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);

      if (window.newsletterPopupEditorOpen) {
        this.open();
      }
    }
  }

  customElements.define('newsletter-popup', NewsletterPopup);
}

